import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchServiceDetailsLatestAPI } from "../../../../shared/hooks/useFetchServiceDetails";
import API_URLS from "../../Utils/axr";
import { errorConditionGTM } from "../../Utils/googleTags";
import store from "../store";
import {
  compareString,
  getServiceAddressFromUser,
  shouldSkipFailedOutApiFetch,
  shouldStopApiRetry,
  logApiRetryCapDwLog,
} from "../../Utils/utils";
import { dwLogging } from "../../../../shared/hooks/useFetchLogin";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import DwLogutil from "../../Utils/DwLogutil";
import { RootState } from "../store";
import { createNotification72hContext } from "../../Utils/notification72HourContext";
import { waitForApiCall } from "../../Utils/waitForApiCall";

type serviceDetailsInitialStateType = {
  serviceDetails: any;
  loading: boolean;
  error: any;
  fetched: boolean;
  retryCount: number;
  latestRequestId: string | null;
};
const initialState: serviceDetailsInitialStateType = {
  serviceDetails: null,
  loading: false,
  error: null,
  fetched: false,
  retryCount: 0,
  latestRequestId: null,
};

export const getserviceDetails = createAsyncThunk(
  "service/details",
  async (user: any) => {
    const { billingAccountNumber, serviceAddrId = "" } = user || {};
    try {
      const reduxStore = store.getState();
      const notificationState = await waitForApiCall(
        store,
        (state: RootState) => state.notificationDetails,
        (nd: any) =>
          nd?.fetched === true &&
          nd?.notificationLoading === false,
        4000
      );
      const notificationsResolved = notificationState?.fetched === true; 
      const notifications = notificationState?.notificationResult?.notifications || [];
      const headers = reduxStore?.brightspeedToken?.headers;
      const serviceAddLocal = getServiceAddressFromUser(user);
      const userType = reduxStore?.user?.data;
      const isPostpaidFiber = reduxStore?.brightSpeedUserUtils?.isPostPaidFiberUser;
      const {companyName,sessionId} = reduxStore?.user;
      let url=API_URLS['getServiceDetails_with_ban']

      const serviceDetailsRequestHeaders = headers;

      const response = await fetchServiceDetailsLatestAPI(
        {
          "": `${url}?ban=${billingAccountNumber}&addressId=${serviceAddrId || serviceAddLocal?.addressId}`,
        },
        serviceDetailsRequestHeaders
      );

      const productTracking = response?.data?.subscriptions?.[0]?.productTracking;  
      const deliveredOrdersMissingNotification: string[] = [];

      const {
        deliveredMap,
        closedOrders,
        isWithin72Hours,
      } = createNotification72hContext(notifications, {
        deliveredKey: "allOrdersDelivered",
        closedKey: "ordersClosed",
      });

      const filteredProductTracking = Array.isArray(productTracking)
        ? productTracking.filter((item: any) => {
          const status = item?.shipmentStatus?.trim()?.toLowerCase();
          const orderNumber = item?.orderNumber;

          if (closedOrders.has(orderNumber)) {
            return false;
          }

          const isDelivered = status === "delivered";

          if (!isDelivered) {
            return true;
          }

          const notificationCreatedDate =
            deliveredMap.get(orderNumber);

          if (!notificationCreatedDate) {
            if (orderNumber && notificationsResolved) {
              deliveredOrdersMissingNotification.push(orderNumber);
            }

            return true;
          }

          return isWithin72Hours(notificationCreatedDate);
        })
        : [];

      response.data.deliveredOrdersMissingNotification = [...new Set(deliveredOrdersMissingNotification)];

      response.data.trackerOrderNumbersForCloseFunction = [
        ...new Set(
          filteredProductTracking
            ?.map(item => item?.orderNumber)
            ?.filter(Boolean)
        )
      ];

      response.data.productTrackingExist =
        Array.isArray(filteredProductTracking) &&
        filteredProductTracking.length > 0 &&
        filteredProductTracking.some(obj => obj && Object.keys(obj)?.length > 0 );

      const DWLOG=(stepname:any)=>{
            dwLogging(
              {
                "": API_URLS?.dwLoggingUrl,
              },
              {
                sessionId: sessionStorage?.getItem("sessionId") || "",
                stepName: stepname,
                cta: "failed",
                customerType: localStorage?.getItem("companyName") || "",
              },
              headers
            );
          }

      if (Array.isArray(filteredProductTracking) && filteredProductTracking.length > 0) {
        filteredProductTracking.forEach((obj, index) => {
          const missingFields = [];

          const isEmpty = (value: any) => !value || value.trim() === "";

          if (isEmpty(obj?.trackingNumber)) missingFields?.push("trackingNumber");
          if (isEmpty(obj?.shipmentStatus)) missingFields?.push("shipmentStatus");
          if (isEmpty(obj?.serviceName)) missingFields?.push("serviceName");
          if (isEmpty(obj?.orderNumber)) missingFields?.push("orderNumber");

          if (missingFields.length > 0) {
            const messageTemplate = EC_DW_LOGGING_STEPS?.eont_value_missing;
            const message = messageTemplate.replace("<missingField>", missingFields.join(", "));
            DWLOG(message);
          }
        });
      }

      const shipmentStepMap: Record<string, number> = {
        "ordered": 1,
        "processing": 1,
        "order processing": 1,
        "shipped": 2,
        "Order shipped": 2,
        "in transit": 3,
        "out for delivery": 3,
        "delivered": 4,
      };

      if (Array.isArray(filteredProductTracking) && filteredProductTracking.length > 0) {

        const allDelivered =
          Array.isArray(productTracking) &&
          productTracking.length > 0 &&
          productTracking.every(
            tracking =>
              tracking?.shipmentStatus?.trim()?.toLowerCase() === "delivered"
          );
        response.data.allShipmentsDelivered = allDelivered;

        const routerTracking = filteredProductTracking.filter(
          (tracking: {serviceName?: string; orderNumber?: string }) =>
            tracking?.serviceName?.trim()?.toLowerCase() !== "wifi extender" 
        )?.at(-1);
        if (routerTracking) {
          const step = shipmentStepMap[routerTracking.shipmentStatus?.trim()?.toLowerCase()] ?? 1;
          response.data.routerTracking = {
            trackingNumber: routerTracking.trackingNumber ?? "",
            shipmentStep: step ,
            serviceName: "router",
            orderNumber: routerTracking?.orderNumber
          };
        }        

        const wifiExtenderTrackings = filteredProductTracking.filter(
          (tracking: {serviceName?: string; orderNumber?: string }) =>
            (tracking?.serviceName?.trim()?.toLowerCase() === "wifi extender" || compareString(tracking?.serviceName?.trim(),"Whole Home WiFi") || compareString(tracking?.serviceName?.trim(),"WHWiFi") || compareString(tracking?.serviceName?.trim(),"Additional WiFi"))
        );

        if (wifiExtenderTrackings.length > 0) {
          const firstExtender = wifiExtenderTrackings[0];
          const shipmentStatus = shipmentStepMap[(firstExtender.shipmentStatus2?.trim()?.toLowerCase() || firstExtender.shipmentStatus?.trim()?.toLowerCase())] ?? 1
          const trackingNumber = firstExtender.trackingNumber2 || firstExtender.trackingNumber
          const orderNumber = firstExtender?.orderNumber2 || firstExtender?.orderNumber;
          response.data.wifiExtenderTracking = {
            trackingNumber: trackingNumber ?? "",
            shipmentStep: shipmentStatus ,
            serviceName: "wifi extender",
            orderNumber: orderNumber,
          };
        }
      }
      
      let ataProdIndex = null;
      if (response?.data?.subscriptions[0]?.serviceDetails?.length > 0) {
        response.data.subscriptions[0].serviceDetails =
          response?.data?.subscriptions[0]?.serviceDetails?.map((item: any) => {
            if (item?.serviceName) {
              item.serviceName = item?.serviceName
                ?.toString()
                ?.trim()
                ?.replace("fQF ", "");
            }
            return item;
          });
      }
      
      if(reduxStore?.user?.isGrandFather){
      response.data.subscriptions[0].serviceDetails= response.data.subscriptions[0].serviceDetails.filter((item:any)=>!compareString(item.serviceName,"controls"));
      // response.data.subscriptions[0].serviceDetails= response.data.subscriptions[0].serviceDetails.filter((item:any)=>!compareString(item.serviceName,"wifi"));
      }
      
      const ataProduct = response?.data?.subscriptions[0]?.serviceDetails?.find(
        (item: any, i: number) => {
          if (
            item?.serviceType?.trim()?.toUpperCase() === "DEVICE TYPE" &&
            item?.serviceState?.trim()?.toUpperCase() === "ACTIVE"
          ) {
            ataProdIndex = i;
            return item;
          }
        }
      );

          // if ((compareString(element?.serviceLabel,"WHWiFi package 1")  || compareString(element?.serviceLabel,"Whole Home WiFi Package1")) && element){
          //       localStorage.setItem("whole_home_p1",JSON.stringify(element))
          //     }
          //     if ((compareString(element?.serviceLabel,"WHWiFi package 2")  || compareString(element?.serviceLabel,"Whole Home WiFi Package2")) && element){
          //       localStorage.setItem("whole_home_p2",JSON.stringify(element))
          //     }
          //     if (compareString(element?.serviceLabel,"Additional WiFi Extender") && element){
          //       localStorage.setItem("whole_home_p3",JSON.stringify(element))
          //     }

          // debugger
      const has_whp1= response?.data?.subscriptions[0]?.serviceDetails?.find((item:any)=>{
        return item.serviceState==="Active" && (compareString(item.serviceLabel,"WHWiFi package 1") || item.serviceLabel==="Whole Home WiFi Package1")
      });
      const has_whp2= response?.data?.subscriptions[0]?.serviceDetails?.find((item:any)=>{
        return item.serviceState==="Active" && (compareString(item.serviceLabel,"WHWiFi package 2") || item.serviceLabel==="Whole Home WiFi Package2")
      });
      const has_whp3= response?.data?.subscriptions[0]?.serviceDetails?.find((item:any)=>{
        return item.serviceState==="Active" && compareString(item.serviceLabel,"Additional WiFi Extender") 
      });

      if(typeof has_whp1 ==="object"){
        // alert();
         DwLogutil({ headers, sessionId, email: user?.email, companyName: companyName, stepName:EC_DW_LOGGING_STEPS?.USER_LOG_WITH_WHWIFI});
      }
      else if(typeof has_whp3 !=="object" && typeof has_whp2 ==="object"){
        // alert();
         DwLogutil({ headers, sessionId, email: user?.email, companyName: companyName, stepName:EC_DW_LOGGING_STEPS?.USER_LOG_WITH_WHWIF2});
      }
      else if(typeof has_whp3 ==="object"){
         DwLogutil({ headers, sessionId, email: user?.email, companyName: companyName, stepName:EC_DW_LOGGING_STEPS?.USER_LOG_WITH_WHWIF2});
      }

      if (response?.data?.subscriptions?.length > 0) {
      for (let index = 0; index < response?.data?.subscriptions[0]?.serviceDetails?.length; index++) {
        const element = response?.data?.subscriptions[0]?.serviceDetails[index];
        if (element?.serviceState === "Active" &&  element?.serviceName?.toLowerCase()?.includes("inside wire protection")) {
          response.data.iwpServiceExists = true;
        }
      }
    }
 
      if(response?.data?.subscriptions?.length > 0 && response?.data?.subscriptions[0]?.serviceDetails) {
        let arrServices = response?.data?.subscriptions[0]?.serviceDetails?.filter((item: any) => item?.serviceState !== "Inactive" && item?.serviceName !== "Quantum Fiber Tech Installation" && item?.serviceType?.toUpperCase() !== "DEVICE TYPE" && item?.isParent?.toLowerCase() === "true"
          && item?.productType?.toLowerCase() === "wa");
        response.data.serviceIDForAxonAPI = arrServices?.length > 0 ? arrServices[0]?.serviceId : ""; 
      }
      if (ataProduct?.serviceName && ataProduct?.serviceId) {
        response.data.ataProduct = ataProduct;
        response.data?.subscriptions[0].serviceDetails?.splice(ataProdIndex, 1);
      }
      // find ProductType logic for timeslot API
      if (
        response?.data?.subscriptions?.length > 0 &&
        response?.data?.subscriptions[0]?.serviceDetails?.length > 0
      ) {
        let internet = false;
        let voice = false;
        for (
          let index = 0;
          index < response?.data?.subscriptions[0]?.serviceDetails?.length;
          index++
        ) {
          const element =
            response?.data?.subscriptions[0]?.serviceDetails[index];
          if (element?.productType?.toUpperCase() === "WA") {
            internet = true;
          } else if (element?.productType?.toUpperCase() === "IL") {
            voice = true;
          }
        }
        if (internet && voice) {
          response.data.productType = "HSI_VOICE";
        } else if (internet && !voice) {
          response.data.productType = "HSI";
        } else if (!internet && voice) {
          response.data.productType = "VOICE";
        }
      }
      //Network settings link display changes
      if (response?.data?.subscriptions?.length > 0 && response?.data?.subscriptions[0]?.serviceDetails?.length > 0) {
        let activeInternet = response?.data?.subscriptions[0]?.serviceDetails?.filter((item: any) => item?.serviceState?.toUpperCase() === "ACTIVE" && item?.isParent?.toUpperCase() === "TRUE" && item?.productType?.toUpperCase() === "WA");
        let activeVoice = response?.data?.subscriptions[0]?.serviceDetails?.filter((item: any) => item?.serviceState?.toUpperCase() === "ACTIVE" && item?.isParent?.toUpperCase() === "TRUE" && item?.productType?.toUpperCase() === "IL");        
        if (activeInternet.length === 0 && activeVoice.length > 0) {
          response.data.voiceOnly = true;
        }
      }
      const pendingService =
        (response?.data?.subscriptions?.length > 0 &&
          response?.data?.subscriptions[0]?.serviceDetails?.find(
            (item: any) => {
              if (
              
               ["INSTALLATION PENDING"]?.includes(
                  item?.serviceState?.toUpperCase() && item?.serviceName?.toLowerCase().includes("internet")
                )
              ) {
                return item;
              }
            }
          )) ||
        null;

        const pendingServiceChange =
        (response?.data?.subscriptions?.length > 0 &&
          response?.data?.subscriptions[0]?.serviceDetails?.find(
            (item: any) => {
              if (
                reduxStore?.user?.data?.currentStatus === "Active" &&
                ["CHANGE PENDING","INACTIVATION PENDING"]?.includes(
                  item?.serviceState?.toUpperCase()
                )
              ) {
                return item;
              }
            }
          )) ||
        null;

      const isMovingFromAccount = response?.data?.order?.length > 0 &&
        response?.data?.order?.find((item: any) => item?.orderType?.toUpperCase() === "FROM" && 
         (item?.orderStatus?.toUpperCase() === "IN PROGRESS" || item?.orderStatus?.toUpperCase() === "IN_PROGRESS" || item?.orderStatus?.toUpperCase() === "ON HOLD" ||
           item?.orderStatus?.toUpperCase() === "FALLOUT"))
      if (isMovingFromAccount) {
        response.data.isMovingFromAccount = isMovingFromAccount;
        DwLogutil({stepName : "Moving From account user logged in"})
      }
      const isMovingToAccount = response?.data?.order?.length > 0 &&
        response?.data?.order?.find((item: any) => item?.orderType?.toUpperCase() === "TO" && 
        (item?.orderStatus?.toUpperCase() === "IN PROGRESS" || item?.orderStatus?.toUpperCase() === "IN_PROGRESS" || item?.orderStatus?.toUpperCase() === "ON HOLD" ||
           item?.orderStatus?.toUpperCase() === "FALLOUT")
      )
      const moveOrderStatus = response?.data?.order?.some((item: any) => item?.orderType?.toUpperCase() === "TO" && item?.orderStatus?.toUpperCase() === "COMPLETED")
      if (moveOrderStatus) {
         response.data.moversPPFActive = true;
         DwLogutil({stepName : "Movers PPF user logged in"})
      }

      if (isMovingToAccount) {
        response.data.isMovingToAccount = isMovingToAccount;
        DwLogutil({stepName : "Moving To account user logged in"})
      }
        
      const isctlUser = headers?.siteId?.toLowerCase() === "ctl";
      const isFiberUser = headers?.siteId?.toLowerCase() === "qf-ii" || headers?.siteId?.toLowerCase() === "qf" || headers?.siteId?.toLowerCase() === "bs";
      const isUserAccountActive = userType?.currentStatus?.toUpperCase() === "ACTIVE";

      const installationPending = () => {
        const isInstallationPending = response?.data?.subscriptions?.[0]?.serviceDetails?.some((service: { serviceState?: any, serviceName?: any }) =>
            service.serviceState?.toUpperCase().includes("INSTALLATION PENDING") &&
            ["VOICE", "INTERNET"].some((type) => service?.serviceName?.trim().toUpperCase().includes(type))
          );
        const isParentTrueInstallationPending = response?.data?.subscriptions?.[0]?.serviceDetails?.some((service: { serviceState?: any, serviceName?: any, isParent?: any }) =>
            service.isParent?.toUpperCase().includes("TRUE") &&
            service.serviceState?.toUpperCase().includes("INSTALLATION PENDING") &&
            ["VOICE", "INTERNET"].some((type) => service?.serviceName?.trim().toUpperCase().includes(type))
          );
        if ((isFiberUser && isInstallationPending) || (isctlUser && isParentTrueInstallationPending)) {
          localStorage.removeItem("changePending");
          response.data.serviceIsInPending = true;
          const stepName = EC_DW_LOGGING_STEPS?.ppf_pending_installation;
          isPostpaidFiber && DwLogutil({ headers, sessionId, email: user?.email, companyName: companyName, stepName });
        }
      }

      const changesPending = () => {
        const isChangesPending = response?.data?.subscriptions?.[0]?.serviceDetails?.some((service: { serviceState?: any, serviceName?: any }) =>
           (service.serviceState?.toUpperCase().includes("CHANGE PENDING") || service.serviceState?.toUpperCase().includes("INACTIVATION PENDING")) &&
            ["VOICE", "INTERNET","VALUEADDEDSERVICES", "PROTECTION", "SUPPORT", "EXTENDER"].some((type) => service?.serviceName?.trim().toUpperCase().includes(type))
          );
        const isParentTureChangesPending = response?.data?.subscriptions?.[0]?.serviceDetails?.some((service: { serviceState?: any, serviceName?: any, isParent?: any }) =>
            service.isParent?.toUpperCase().includes("TRUE") &&
            (service.serviceState?.toUpperCase().includes("CHANGE PENDING") || service.serviceState?.toUpperCase().includes("INACTIVATION PENDING")) &&
            ["VOICE", "INTERNET"].some((type) => service?.serviceName?.trim().toUpperCase().includes(type))
          );
        if (isUserAccountActive && ((isFiberUser && isChangesPending) || (isctlUser && isParentTureChangesPending))) {
          response.data.pendingServiceChange = true;
        }
      };

      if (response?.data?.subscriptions?.length > 0) {
        changesPending();
        installationPending();
      }
      if (pendingServiceChange?.serviceId) { 
        localStorage.removeItem("changePending");
         response.data.pendingServiceChange = true;
      }

      if (pendingService?.serviceId) {
        localStorage.removeItem("changePending");
        response.data.serviceIsInPending = true;
      } else if (
        !pendingService?.serviceId &&
        serviceAddLocal?.addressId?.includes(localStorage.getItem("changePending"))
      ) {
        response.data.pendingServiceChange = true;
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  {
    condition: (user, { getState }) => {
      const state = getState() as RootState;
      const sd = state.serviceDetails;
      if (state.brightspeedToken?.headers?.flowType === "NA") {
        return false;
      }
      if (!user?.billingAccountNumber) {
        return false;
      }
      if (sd.loading) {
        return false;
      }
      // Stop infinite retry when fetch never succeeds; allow refresh when data exists
      if (shouldSkipFailedOutApiFetch(!!sd.serviceDetails, sd.fetched)) {
        return false;
      }
      if (shouldStopApiRetry(sd.retryCount) && !sd.serviceDetails) {
        return false;
      }
      return true;
    },
  }
);

const serviceDetailSlice = createSlice({
  name: "service/details",
  initialState,
  reducers: {
    clearServiceDetailSlice: (state) => {
      return initialState;
    },
    serviceDetailSliceSetInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getserviceDetails.pending, (state, action) => {
      state.loading = true;
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(getserviceDetails.fulfilled, (state, action) => {
      const isLatest = action.meta.requestId === state.latestRequestId;
      // An older 200 must not replace data already applied by a newer request
      if (!isLatest && state.serviceDetails) {
        return;
      }
      state.serviceDetails = action.payload;
      state.error = null;
      state.retryCount = 0;
      state.fetched = true;
      if (isLatest) {
        state.loading = false;
      }
    });
    builder.addCase(getserviceDetails.rejected, (state, action) => {
      // Skipped because another call is in flight — do not clear loading or count as a failure
      if (action.meta?.condition) {
        return;
      }
      // Stale response from an older overlapping call
      if (action.meta.requestId !== state.latestRequestId) {
        return;
      }
      state.loading = false;
      // A later 401 must not wipe a 200 that already landed
      if (state.serviceDetails) {
        return;
      }
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "service details",
        action.error?.message,
        action?.type
      );
      state.serviceDetails = null;
      state.error = action.error;
      state.retryCount += 1;
      if (shouldStopApiRetry(state.retryCount)) {
        state.fetched = true;
        logApiRetryCapDwLog(
          EC_DW_LOGGING_STEPS.get_service_details_API_retry_cap,
          action.error?.message
        );
      }
    });
  },
});

export const { clearServiceDetailSlice, serviceDetailSliceSetInitialState } =
  serviceDetailSlice.actions;
export default serviceDetailSlice.reducer;
